/**
 * Batch List Page
 * Displays all inventory batches with filtering, search, and multiple view modes
 * Includes Grid View, Table View, Alerts, and Expiring Batches tabs
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';
import { getMockBatches } from '@/services/commerce/mockBatchData';
import type { BatchResponse, InventoryBatchStatus, ProductType } from '@/types/inventory';
import { ProductTypeConfig, BatchStatusConfig } from '@/types/inventory';
import { BatchSummaryCard } from '@/components/inventory/batches/BatchSummaryCard';
import { BatchDetailsDialog } from '@/components/inventory/batches/BatchDetailsDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  Search,
  Filter,
  AlertCircle,
  Package,
  RefreshCw,
  AlertTriangle,
  Clock,
  Eye,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface SortConfig {
  field: keyof BatchResponse;
  order: 'ASC' | 'DESC';
}

export default function BatchListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [batches, setBatches] = useState<BatchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<InventoryBatchStatus | 'ALL'>('ALL');
  const [productTypeFilter, setProductTypeFilter] = useState<ProductType | 'ALL'>('ALL');

  // View and sort states
  const [activeTab, setActiveTab] = useState('grid');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'batchNumber', order: 'ASC' });

  // Fetch batches
  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await inventoryBatchService.getAllBatches();
      setBatches(data);

      // Show success toast if returning from batch creation
      const state = location.state as any;
      if (state?.refreshData) {
        toast.success('Batch Created Successfully!', {
          description: 'Your new batch has been added to the list.',
          duration: 4000
        });
        // Clear the state to prevent showing the toast on every re-render
        window.history.replaceState({}, document.title);
      }
    } catch (error: any) {
      console.error('Failed to fetch batches from API, using mock data:', error);

      // Use mock data as fallback
      const mockData = getMockBatches();
      setBatches(mockData);

      // Show a warning that we're using mock data
      setError('Using sample data (Backend API unavailable)');
      toast.warning('Using Sample Data', {
        description: 'Backend API is not available. Displaying sample batch data for testing.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      const data = await inventoryBatchService.getAllBatches();
      setBatches(data);

      toast.success('Refreshed successfully!', {
        description: 'Inventory batches data has been updated.',
        duration: 3000
      });
    } catch (error: any) {
      console.error('Failed to refresh batches:', error);

      const mockData = getMockBatches();
      setBatches(mockData);
      setError('Using sample data (Backend API unavailable)');

      toast.warning('Using Sample Data', {
        description: 'Backend API is not available. Displaying sample batch data.'
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [location.state]);

  // Helper function to calculate days remaining
  const getDaysRemaining = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysRemaining = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysRemaining;
  };

  // Helper function to sort batches
  const sortBatches = (batchesToSort: BatchResponse[]) => {
    const sorted = [...batchesToSort].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;
      if (typeof aValue === 'string') {
        comparison = aValue.localeCompare(String(bValue));
      } else {
        comparison = Number(aValue) - Number(bValue);
      }

      return sortConfig.order === 'ASC' ? comparison : -comparison;
    });
    return sorted;
  };

  // Statistics
  const stats = useMemo(() => {
    return {
      total: batches.length,
      available: batches.filter((b) => b.batchStatus === 'AVAILABLE').length,
      allocated: batches.filter((b) => b.batchStatus === 'ALLOCATED').length,
      expired: batches.filter((b) => b.batchStatus === 'EXPIRED').length,
      alerts: batches.filter((b) => b.batchStatus === 'EXPIRED' || getDaysRemaining(b.expiryDate) <= 7).length
    };
  }, [batches]);

  // Filter and search logic
  const filteredBatches = useMemo(() => {
    let result = batches.filter((batch) => {
      if (searchQuery === '') {
        // No search query — match all
      } else {
        // Build a combined searchable string from all batch fields
        const productConfig = ProductTypeConfig[batch.productType];
        const statusConfig = BatchStatusConfig[batch.batchStatus];

        const searchableText = [
          batch.batchNumber || '',
          batch.storageLocation || '',
          batch.productType || '',
          productConfig?.label || '',
          productConfig?.abbreviation || '',
          batch.batchStatus || '',
          statusConfig?.label || '',
          batch.qualityGrade || ''
        ].join(' ').toLowerCase().replace(/[_-]/g, ' ');

        // Split query into words and check ALL words match somewhere in the searchable text
        const queryWords = searchQuery.toLowerCase().replace(/[_-]/g, ' ').trim().split(/\s+/);
        const allWordsMatch = queryWords.every((word) => searchableText.includes(word));

        if (!allWordsMatch) {
          return false;
        }
      }

      const matchesStatus =
        statusFilter === 'ALL' || batch.batchStatus === statusFilter;

      const matchesProductType =
        productTypeFilter === 'ALL' || batch.productType === productTypeFilter;

      return matchesStatus && matchesProductType;
    });

    // For active tab, apply specific filtering
    if (activeTab === 'alerts') {
      result = result.filter((b) => b.batchStatus === 'EXPIRED' || getDaysRemaining(b.expiryDate) <= 7);
    } else if (activeTab === 'expiring') {
      result = result.filter((b) => {
        const daysRemaining = getDaysRemaining(b.expiryDate);
        return daysRemaining > 0 && daysRemaining <= 30 && b.batchStatus !== 'EXPIRED';
      });
    }

    return sortBatches(result);
  }, [batches, searchQuery, statusFilter, productTypeFilter, activeTab, sortConfig]);

  const handleSort = (field: keyof BatchResponse) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Inventory Batches</h1>
                <p className="text-gray-600 mt-2">
                  Manage and track biogas inventory batches
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                >
                  <RefreshCw className={cn('mr-2 h-5 w-5', refreshing && 'animate-spin')} />
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  onClick={() => navigate('/inventory/batches/create')}
                  size="lg"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Create Batch
                </Button>
              </div>
            </div>
          </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Total Batches
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.available}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Allocated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.allocated}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Expired
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.expired}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-3xl font-bold text-red-600">{stats.alerts}</div>
              {stats.alerts > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {stats.alerts}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area - Sidebar + Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-gray-600" />
                  <CardTitle>Filters</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search batch, product type, location, status..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Statuses</SelectItem>
                      {Object.entries(BatchStatusConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span>{config.icon}</span>
                            <span>{config.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Type Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Product Type</label>
                  <Select value={productTypeFilter} onValueChange={(value) => setProductTypeFilter(value as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Product Types</SelectItem>
                      {Object.entries(ProductTypeConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            <span>{config.icon}</span>
                            <span>{config.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters Button */}
                {(searchQuery || statusFilter !== 'ALL' || productTypeFilter !== 'ALL') && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('ALL');
                      setProductTypeFilter('ALL');
                    }}
                  >
                    Clear All Filters
                  </Button>
                )}

                {/* Filter Summary */}
                <div className="pt-4 border-t">
                  <div className="text-xs text-gray-600">
                    Showing <span className="font-semibold text-gray-900">{filteredBatches.length}</span> of <span className="font-semibold text-gray-900">{batches.length}</span> batches
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Warning/Error State */}
            {error && (
              <Alert className={cn('mb-8',
                error.includes('sample data')
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-red-300 bg-red-50'
              )}>
                {error.includes('sample data') ? (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                )}
                <AlertDescription className={cn(
                  error.includes('sample data') ? 'text-amber-800' : 'text-red-800'
                )}>
                  {error}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={fetchBatches}
                    className={cn(
                      'ml-2 underline',
                      error.includes('sample data') ? 'text-amber-700' : 'text-red-700'
                    )}
                  >
                    <RefreshCw className="mr-1 h-3 w-3" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Tabs for different views */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="grid">Grid View</TabsTrigger>
                <TabsTrigger value="table">Table View</TabsTrigger>
                <TabsTrigger value="alerts" className="flex items-center gap-2">
                  Alerts
                  {stats.alerts > 0 && (
                    <Badge variant="destructive" className="ml-1">{stats.alerts}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="expiring" className="flex items-center gap-2">
                  Expiring Batches
                  <Clock className="h-4 w-4" />
                </TabsTrigger>
              </TabsList>

              {/* Loading State */}
              {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Card key={i}>
                      <CardHeader>
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-1/2 mt-2" />
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!loading && filteredBatches.length === 0 && (
                <Card className="py-16">
                  <CardContent className="flex flex-col items-center justify-center text-center">
                    <Package className="h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      No batches found
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md">
                      {activeTab === 'alerts' && 'No critical batches or expiring batches'}
                      {activeTab === 'expiring' && 'No batches expiring within 30 days'}
                      {activeTab === 'grid' && 'Get started by creating your first inventory batch'}
                      {activeTab === 'table' && 'No batches to display in table view'}
                    </p>
                    {(activeTab === 'grid' || activeTab === 'table') && (
                      <Button onClick={() => navigate('/inventory/batches/create')}>
                        <Plus className="mr-2 h-5 w-5" />
                        Create First Batch
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Grid View Tab */}
              <TabsContent value="grid" className="mt-0">
                {!loading && filteredBatches.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBatches.map((batch) => (
                      <BatchSummaryCard
                        key={batch.id}
                        batch={batch}
                        onViewDetails={() => {
                          setSelectedBatchId(batch.id);
                          setIsDetailsDialogOpen(true);
                        }}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Table View Tab */}
              <TabsContent value="table" className="mt-0">
                {!loading && filteredBatches.length > 0 && (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer" onClick={() => handleSort('batchNumber')}>
                            <div className="flex items-center gap-2">
                              Batch #
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Product Type</TableHead>
                          <TableHead className="cursor-pointer" onClick={() => handleSort('currentQuantity')}>
                            <div className="flex items-center gap-2">
                              Quantity
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="cursor-pointer" onClick={() => handleSort('expiryDate')}>
                            <div className="flex items-center gap-2">
                              Expiry Date
                              <ArrowUpDown className="h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Days Remaining</TableHead>
                          <TableHead>Storage Location</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBatches.map((batch) => {
                          const daysRemaining = getDaysRemaining(batch.expiryDate);
                          const productConfig = ProductTypeConfig[batch.productType] || {
                            icon: '📦',
                            label: batch.productType || 'Unknown'
                          };
                          const statusConfig = BatchStatusConfig[batch.batchStatus] || {
                            icon: '?',
                            label: batch.batchStatus
                          };

                          return (
                            <TableRow key={batch.id} className="hover:bg-gray-50">
                              <TableCell className="font-medium">{batch.batchNumber}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span>{productConfig.icon}</span>
                                  <span>{productConfig.label}</span>
                                </div>
                              </TableCell>
                              <TableCell>{batch.currentQuantity} {batch.quantityUnit}</TableCell>
                              <TableCell>
                                <Badge>
                                  <span className="mr-1">{statusConfig.icon}</span>
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>{format(new Date(batch.expiryDate), 'MMM dd, yyyy')}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={daysRemaining <= 7 ? 'destructive' : daysRemaining <= 30 ? 'outline' : 'secondary'}
                                  className={cn(
                                    daysRemaining <= 0 && 'bg-red-600 text-white'
                                  )}
                                >
                                  {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days`}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">{batch.storageLocation}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedBatchId(batch.id);
                                    setIsDetailsDialogOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </TabsContent>

              {/* Alerts Tab */}
              <TabsContent value="alerts" className="mt-0">
                {!loading && filteredBatches.length > 0 && (
                  <div className="space-y-4">
                    {filteredBatches.map((batch) => {
                      const daysRemaining = getDaysRemaining(batch.expiryDate);
                      const isExpired = daysRemaining < 0;
                      const isCritical = daysRemaining >= 0 && daysRemaining <= 7;

                      return (
                        <Alert key={batch.id} className={cn(
                          'border-l-4',
                          isExpired ? 'border-l-red-600 bg-red-50' : 'border-l-orange-500 bg-orange-50'
                        )}>
                          <AlertTriangle className={cn(
                            'h-4 w-4',
                            isExpired ? 'text-red-600' : 'text-orange-600'
                          )} />
                          <AlertDescription className={cn(
                            isExpired ? 'text-red-800' : 'text-orange-800'
                          )}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold">{batch.batchNumber}</h4>
                                <p className="text-sm mt-1">
                                  {isExpired
                                    ? `⚠️ EXPIRED ${Math.abs(daysRemaining)} days ago`
                                    : `⚠️ Expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`
                                  }
                                </p>
                                <p className="text-sm">Expiry Date: {format(new Date(batch.expiryDate), 'MMM dd, yyyy')}</p>
                                <p className="text-sm">Storage: {batch.storageLocation}</p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedBatchId(batch.id);
                                  setIsDetailsDialogOpen(true);
                                }}
                                className={cn(
                                  isExpired
                                    ? 'border-red-600 text-red-600 hover:bg-red-50'
                                    : 'border-orange-600 text-orange-600 hover:bg-orange-50'
                                )}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Expiring Batches Tab */}
              <TabsContent value="expiring" className="mt-0">
                {!loading && filteredBatches.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredBatches.map((batch) => {
                      const daysRemaining = getDaysRemaining(batch.expiryDate);

                      return (
                        <Card key={batch.id} className="border-l-4 border-l-amber-500">
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg">{batch.batchNumber}</CardTitle>
                                <p className="text-sm text-gray-600 mt-1">
                                  <Clock className="h-4 w-4 inline mr-2 text-amber-600" />
                                  {daysRemaining} days remaining
                                </p>
                              </div>
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                                {Math.round((daysRemaining / 30) * 100)}%
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm text-gray-600">Expires</p>
                                <p className="font-semibold">{format(new Date(batch.expiryDate), 'MMM dd, yyyy')}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Available Quantity</p>
                                <p className="font-semibold">{batch.currentQuantity} {batch.quantityUnit}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Storage Location</p>
                                <p className="font-semibold text-sm">{batch.storageLocation}</p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedBatchId(batch.id);
                                  setIsDetailsDialogOpen(true);
                                }}
                                className="w-full mt-4"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
